import React, { memo, FunctionComponent, ReactElement, RefObject } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import Channel from '../../../TeaserInfo/Channel'
import CaptionFont from '../../../Theme/Fonts/Caption'
import VerticalSpacer from '../../../Layouts/Spacer/Vertical'
import Divider from '../../../BaseComponents/Divider'
import SmallTeaserHeadlineFont from '../../../Theme/Fonts/SmallTeaserHeadline'
import { TintColor } from '../../../BaseComponents/Image/ImageInterfaces'
import { RequireAtLeastOne } from '../../../utils/types/requireAtLeastOne'
import LinkProps from '../../../Link/LinkProps'
import VideoLabelProps from '../../../BaseComponents/Labels/Video/VideoLabelProps'
import NewsTickerEntryImage from './Image'

type Channel = {
    /** url of channel image */
    imageUrl: string
    /** info for seo about imageUrl */
    alt?: string
    /** channel name */
    name: string
}

export type Props = {
    /** published text */
    publishedAt: string
    /** corresponding channel */
    channel: Channel
    /** headline text */
    headline: string
    /** href string to navigate to the provided url (web only) */
    href?: string
    /**
     * callback function triggered when component is pressed
     *
     * @TJS-type function
     * */
    onPress?: () => void
    /**
     * callback function triggered when component is long pressed
     *
     * @TJS-type function
     * */
    onLongPress?: () => void
    /** inserts a divider at the bottom */
    hasDivider?: boolean
    /** changes the background color */
    selected?: boolean
    /** url of image to be displayed */
    imageUrl?: string
    /** info for seo about imageUrl */
    alt?: string
    /** colors the corresponding image in black or white */
    tintColor?: TintColor
    /**
     * property for ref forwarding
     * @TJS-type React.RefObject<Element>
     * */
    innerRef?: RefObject<Element>
} & LinkProps &
    VideoLabelProps

export type NewsTickerProps = RequireAtLeastOne<Props, 'onPress' | 'href'>

const NewsTickerEntry: FunctionComponent<NewsTickerProps> = ({
    publishedAt,
    channel,
    headline,
    href,
    onPress,
    hasDivider,
    selected,
    imageUrl,
    alt,
    tintColor,
    onLongPress,
    innerRef,
    webRel,
    teaserIcon,
}): ReactElement => {
    const backgroundColor = selected ? 'tahitian-4' : 'pearl'
    return (
        <Grid ref={innerRef} noExpand backgroundColor={backgroundColor} paddingX={5}>
            <Touchable onPress={onPress} href={href} onLongPress={onLongPress} webRel={webRel}>
                <Grid horizontal marginTop={5} noExpand>
                    <Grid width={32} noExpand>
                        <CaptionFont color="keshi-3">{publishedAt}</CaptionFont>
                    </Grid>
                    <Grid>
                        <Grid noExpand horizontal>
                            <Grid marginX={5}>
                                <Channel
                                    name={channel.name}
                                    imageUrl={channel.imageUrl}
                                    alt={alt}
                                    tintColor={tintColor}
                                />
                                <VerticalSpacer space="small" backgroundColor={backgroundColor} />
                                <SmallTeaserHeadlineFont numberOfLines={3}>
                                    {headline}
                                </SmallTeaserHeadlineFont>
                            </Grid>
                            <NewsTickerEntryImage
                                width={94}
                                imageUrl={imageUrl}
                                alt={alt}
                                teaserIcon={teaserIcon}
                            />
                        </Grid>
                        <VerticalSpacer space="standard" backgroundColor={backgroundColor} />
                    </Grid>
                </Grid>
                {hasDivider ? <Divider dark={!selected} /> : undefined}
            </Touchable>
        </Grid>
    )
}

export default memo(NewsTickerEntry)
